const CHANNEL_STATES = Object.freeze({
  IDLE: 'idle',
  OFFLINE: 'offline',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
})

const DEFAULT_OPTIONS = Object.freeze({
  heartbeatIntervalMs: 5000,
  controlFreshMs: 15000,
  audioFreshMs: 3000,
  terminalStatusFreshMs: 15000,
  audioStaleReconnectMs: 15000,
  reconnectBaseDelayMs: 1000,
  reconnectMaxDelayMs: 30000,
  reconnectMaxAttempts: Infinity,
  rediscoverAfterAttempts: 3,
  freshnessCheckIntervalMs: 1000,
})

const DEFAULT_CONSUMER = 'default'

function nowMs() {
  return Date.now()
}

function socketOpenValue() {
  return typeof WebSocket !== 'undefined' && Number.isFinite(WebSocket.OPEN)
    ? WebSocket.OPEN
    : 1
}

function normaliseError(error, fallback) {
  if (!error) return fallback || null
  if (typeof error === 'string') return error
  return error.message || error.type || fallback || 'websocket_error'
}

function clonePayload(value) {
  if (value === null || value === undefined) return null
  if (typeof value !== 'object') return value
  if (Array.isArray(value)) return value.map((item) => clonePayload(item))
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, clonePayload(item)])
  )
}

function normaliseDevice(device) {
  if (!device || typeof device !== 'object') {
    throw new Error('[Rock5aSession] device is required')
  }
  const ip = device.ip || device.host
  if (!ip) throw new Error('[Rock5aSession] device.ip or device.host is required')
  return { ...device, ip }
}

function endpointKey(device) {
  if (!device) return ''
  return [
    device.ip || device.host || '',
    device.port || 8770,
    device.audio_port || 8771,
    device.control_path || '',
    device.audio_path || '',
    device.secure === true || device.ws_protocol === 'wss' ? 'secure' : 'plain',
    device.token || '',
  ].join('|')
}

function ageOf(timestamp, now) {
  return timestamp ? Math.max(0, now - timestamp) : Infinity
}

function freshnessEntry(timestamp, thresholdMs, now) {
  const ageMs = ageOf(timestamp, now)
  return {
    lastFrameAt: timestamp || 0,
    ageMs,
    fresh: ageMs <= thresholdMs,
  }
}

export function getRock5aPairToken(device = {}) {
  if (device && device.token) return String(device.token)
  const configuredToken = process.env.VUE_APP_ASSISTANT_TOKEN || ''
  if (configuredToken) return String(configuredToken)
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const token = window.localStorage.getItem('terminalPairToken')
      return token ? String(token) : ''
    }
  } catch (_) {
    // Storage can be disabled by the browser privacy policy.
  }
  return ''
}

export function buildRock5aWsUrl(device, channel = 'control') {
  const current = normaliseDevice(device)
  const secure = current.secure === true || current.ws_protocol === 'wss'
  const protocol = secure ? 'wss' : 'ws'
  const rawHost = current.ip || current.host
  const host = rawHost.includes(':') && !rawHost.startsWith('[') ? `[${rawHost}]` : rawHost
  const isAudio = channel === 'audio'
  const port = isAudio ? (current.audio_port || 8771) : (current.port || 8770)
  const path = isAudio ? (current.audio_path || '') : (current.control_path || '')
  const normalisedPath = path && !path.startsWith('/') ? `/${path}` : path
  const token = getRock5aPairToken(current)
  const query = token ? `${normalisedPath.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}` : ''
  return `${protocol}://${host}:${port}${normalisedPath}${query}`
}

export function createRock5aPingMessage(timestamp = Date.now()) {
  return JSON.stringify({ type: 'ping', timestamp })
}

export function parseRock5aJsonMessage(raw) {
  if (typeof raw !== 'string') return null
  try {
    return JSON.parse(raw)
  } catch (_) {
    return null
  }
}

export function isRock5aPongMessage(message) {
  const parsed = typeof message === 'string' ? parseRock5aJsonMessage(message) : message
  return Boolean(parsed && parsed.type === 'pong')
}

export class Rock5aDeviceSession {
  constructor(options = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options }
    this._now = options.now || nowMs
    this._createWebSocket = options.webSocketFactory || ((url) => new WebSocket(url))
    this._setTimeout = options.setTimeoutFn || ((fn, delay) => setTimeout(fn, delay))
    this._clearTimeout = options.clearTimeoutFn || ((timer) => clearTimeout(timer))
    this._setInterval = options.setIntervalFn || ((fn, delay) => setInterval(fn, delay))
    this._clearInterval = options.clearIntervalFn || ((timer) => clearInterval(timer))

    this._device = null
    this._consumers = new Set()
    this._audioConsumers = new Set()
    this._listeners = new Map()
    this._rediscoverHook = options.rediscover || null
    this._rediscoveryPromise = null
    this._manualStop = true

    this._controlWs = null
    this._audioWs = null
    this._controlGeneration = 0
    this._audioGeneration = 0
    this._controlOpenedAt = 0
    this._audioOpenedAt = 0

    this._channelState = {
      control: CHANNEL_STATES.OFFLINE,
      audio: CHANNEL_STATES.IDLE,
    }
    this._lastFrameAt = {
      control: 0,
      gesture: 0,
      audio: 0,
      terminalStatus: 0,
    }
    this._reconnectAttempt = { control: 0, audio: 0 }
    this._error = { control: null, audio: null }
    this._lastCloseCode = { control: null, audio: null }
    this._reconnectTimer = { control: null, audio: null }
    this._heartbeatTimer = null
    this._freshnessTimer = null
    this._freshnessSignature = ''
    this._terminalStatus = null
    this._serverInfo = null
    this._audioServerInfo = null
    this._lastSequence = null
  }

  get device() { return this._device }
  get controlWs() { return this._controlWs }
  get audioWs() { return this._audioWs }
  get status() { return this.getStatus() }
  get connected() { return this._channelState.control === CHANNEL_STATES.CONNECTED }
  get audioConnected() { return this._channelState.audio === CHANNEL_STATES.CONNECTED }

  on(event, callback) {
    if (typeof callback !== 'function') return () => {}
    if (!this._listeners.has(event)) this._listeners.set(event, new Set())
    this._listeners.get(event).add(callback)
    return () => this.off(event, callback)
  }

  subscribe(event, callback) {
    return this.on(event, callback)
  }

  off(event, callback) {
    const listeners = this._listeners.get(event)
    if (!listeners) return
    listeners.delete(callback)
    if (listeners.size === 0) this._listeners.delete(event)
  }

  _emit(event, payload) {
    const listeners = this._listeners.get(event)
    if (!listeners) return
    for (const callback of Array.from(listeners)) {
      try {
        callback(payload)
      } catch (error) {
        console.error(`[Rock5aSession] listener failed for ${event}:`, error)
      }
    }
  }

  _emitStatus() {
    this._freshnessSignature = this._getFreshnessSignature()
    this._emit('status', this.getStatus())
  }

  getStatus() {
    const now = this._now()
    const freshness = {
      control: freshnessEntry(this._lastFrameAt.control, this.options.controlFreshMs, now),
      gesture: freshnessEntry(this._lastFrameAt.gesture, this.options.controlFreshMs, now),
      audio: freshnessEntry(this._lastFrameAt.audio, this.options.audioFreshMs, now),
      terminalStatus: freshnessEntry(
        this._lastFrameAt.terminalStatus,
        this.options.terminalStatusFreshMs,
        now
      ),
    }
    return {
      device: this._device ? { ...this._device } : null,
      control: this._channelState.control,
      audio: this._channelState.audio,
      audioRequested: this._audioConsumers.size > 0,
      connected: this._channelState.control === CHANNEL_STATES.CONNECTED,
      ready: this._channelState.control === CHANNEL_STATES.CONNECTED
        && freshness.control.fresh
        && (this._audioConsumers.size === 0 || this._channelState.audio === CHANNEL_STATES.CONNECTED),
      freshness,
      reconnectAttempt: { ...this._reconnectAttempt },
      error: { ...this._error },
      closeCode: { ...this._lastCloseCode },
      terminalStatus: clonePayload(this._terminalStatus),
      serverInfo: clonePayload(this._serverInfo),
      audioServerInfo: clonePayload(this._audioServerInfo),
      protocolVersion: this._serverInfo?.protocolVersion
        || this._terminalStatus?.protocol_version
        || this._device?.protocol_version
        || this._device?.txt?.protocol_version
        || null,
      lastSequence: this._lastSequence,
      pairingRequired: Boolean(
        this._serverInfo?.capabilities?.pairing_required
        || this._terminalStatus?.pairing_required
        || this._error.control === 'invalid_token'
        || this._error.audio === 'invalid_token'
      ),
    }
  }

  setRediscoverHook(callback) {
    this._rediscoverHook = typeof callback === 'function' ? callback : null
  }

  connect(device, consumer = DEFAULT_CONSUMER, options = {}) {
    return this.acquire(device, consumer, options)
  }

  acquire(device, consumer = DEFAULT_CONSUMER, options = {}) {
    const nextDevice = normaliseDevice(device)
    const changed = endpointKey(nextDevice) !== endpointKey(this._device)

    this._consumers.add(consumer)
    if (options.audio === true) this._audioConsumers.add(consumer)
    else if (options.audio === false) this._audioConsumers.delete(consumer)
    this._manualStop = false

    if (changed) this._switchDevice(nextDevice)
    else this._device = { ...this._device, ...nextDevice }

    this._ensureControlConnection()
    if (this._audioConsumers.size > 0) this._ensureAudioConnection()
    this._startFreshnessMonitor()
    this._emitStatus()
    return this.getStatus()
  }

  release(consumer = DEFAULT_CONSUMER) {
    this._consumers.delete(consumer)
    this.releaseAudio(consumer)
    if (this._consumers.size === 0) this.disconnectAll()
  }

  disconnect(consumer) {
    if (arguments.length > 0) this.release(consumer)
    else this.disconnectAll()
  }

  disconnectAll() {
    this._manualStop = true
    this._consumers.clear()
    this._audioConsumers.clear()
    this._clearReconnectTimer('control')
    this._clearReconnectTimer('audio')
    this._stopHeartbeat()
    this._stopFreshnessMonitor()
    this._closeSocket('control', CHANNEL_STATES.OFFLINE)
    this._closeSocket('audio', CHANNEL_STATES.IDLE)
    this._device = null
    this._terminalStatus = null
    this._serverInfo = null
    this._audioServerInfo = null
    this._lastSequence = null
    this._lastFrameAt = { control: 0, gesture: 0, audio: 0, terminalStatus: 0 }
    this._reconnectAttempt = { control: 0, audio: 0 }
    this._error = { control: null, audio: null }
    this._lastCloseCode = { control: null, audio: null }
    this._emitStatus()
  }

  requestAudio(consumer = DEFAULT_CONSUMER) {
    if (!this._device || this._manualStop) return false
    this._audioConsumers.add(consumer)
    this._ensureAudioConnection()
    this._startFreshnessMonitor()
    this._emitStatus()
    return true
  }

  releaseAudio(consumer = DEFAULT_CONSUMER) {
    this._audioConsumers.delete(consumer)
    if (this._audioConsumers.size === 0) {
      this._clearReconnectTimer('audio')
      this._reconnectAttempt.audio = 0
      this._error.audio = null
      this._lastFrameAt.audio = 0
      this._closeSocket('audio', CHANNEL_STATES.IDLE)
      this._emitStatus()
    }
  }

  sendControl(data) {
    return this._send(this._controlWs, data)
  }

  sendAudio(data) {
    return this._send(this._audioWs, data)
  }

  _send(socket, data) {
    if (!socket || socket.readyState !== socketOpenValue()) return false
    try {
      socket.send(data)
      return true
    } catch (error) {
      return false
    }
  }

  reconnectNow(channel = 'control') {
    if (channel !== 'control' && channel !== 'audio') return false
    if (!this._device || this._manualStop) return false
    if (channel === 'audio' && this._audioConsumers.size === 0) return false
    this._clearReconnectTimer(channel)
    this._closeSocket(
      channel,
      channel === 'audio' ? CHANNEL_STATES.RECONNECTING : CHANNEL_STATES.RECONNECTING
    )
    if (channel === 'control') this._ensureControlConnection()
    else this._ensureAudioConnection()
    return true
  }

  async waitForChannel(channel, options = {}) {
    const timeoutMs = Number.isFinite(options.timeoutMs) ? options.timeoutMs : 5000
    const requireFresh = options.fresh === true
    const isReady = (status) => {
      if (!status || status[channel] !== CHANNEL_STATES.CONNECTED) return false
      return !requireFresh || Boolean(status.freshness[channel] && status.freshness[channel].fresh)
    }

    const initial = this.getStatus()
    if (isReady(initial)) return { ok: true, status: initial }

    return await new Promise((resolve) => {
      let settled = false
      const finish = (result) => {
        if (settled) return
        settled = true
        unsubscribe()
        this._clearTimeout(timer)
        resolve(result)
      }
      const unsubscribe = this.on('status', (status) => {
        if (isReady(status)) finish({ ok: true, status })
      })
      const timer = this._setTimeout(() => {
        finish({
          ok: false,
          reason: `${channel}_${requireFresh ? 'stale_or_' : ''}unavailable`,
          status: this.getStatus(),
        })
      }, timeoutMs)
    })
  }

  _switchDevice(device) {
    const previousDevice = this._device ? { ...this._device } : null
    this._clearReconnectTimer('control')
    this._clearReconnectTimer('audio')
    this._stopHeartbeat()
    this._closeSocket('control', CHANNEL_STATES.OFFLINE)
    this._closeSocket('audio', this._audioConsumers.size > 0
      ? CHANNEL_STATES.OFFLINE
      : CHANNEL_STATES.IDLE)
    this._device = device
    this._terminalStatus = null
    this._serverInfo = null
    this._audioServerInfo = null
    this._lastSequence = null
    this._lastFrameAt = { control: 0, gesture: 0, audio: 0, terminalStatus: 0 }
    this._reconnectAttempt = { control: 0, audio: 0 }
    this._error = { control: null, audio: null }
    this._lastCloseCode = { control: null, audio: null }
    this._emit('deviceChanged', {
      previous: previousDevice,
      device: { ...device },
    })
  }

  _ensureControlConnection() {
    if (this._manualStop || !this._device || this._consumers.size === 0) return
    if (this._controlWs && (
      this._channelState.control === CHANNEL_STATES.CONNECTING
      || this._channelState.control === CHANNEL_STATES.RECONNECTING
      || this._channelState.control === CHANNEL_STATES.CONNECTED
    )) return

    const reconnecting = this._reconnectAttempt.control > 0
    this._channelState.control = reconnecting
      ? CHANNEL_STATES.RECONNECTING
      : CHANNEL_STATES.CONNECTING
    this._emitStatus()

    const generation = ++this._controlGeneration
    let socket
    try {
      socket = this._createWebSocket(buildRock5aWsUrl(this._device, 'control'))
    } catch (error) {
      this._error.control = normaliseError(error, 'control_socket_create_failed')
      this._channelState.control = CHANNEL_STATES.OFFLINE
      this._emit('error', { channel: 'control', error: this._error.control })
      this._scheduleReconnect('control')
      return
    }
    this._controlWs = socket

    socket.onopen = () => {
      if (!this._isCurrentSocket('control', socket, generation)) return
      this._channelState.control = CHANNEL_STATES.CONNECTED
      this._error.control = null
      this._controlOpenedAt = this._now()
      this._startHeartbeat()
      this.sendControl(createRock5aPingMessage(this._now()))
      const status = this.getStatus()
      this._emitStatus()
      this._emit('connected', status)
    }

    socket.onmessage = (event) => {
      if (!this._isCurrentSocket('control', socket, generation)) return
      this._handleControlMessage(event.data)
    }

    socket.onerror = (event) => {
      if (!this._isCurrentSocket('control', socket, generation)) return
      this._error.control = normaliseError(event, 'control_websocket_error')
      this._emit('error', { channel: 'control', error: this._error.control, event })
      this._emitStatus()
    }

    socket.onclose = (event) => {
      if (!this._isCurrentSocket('control', socket, generation)) return
      this._controlWs = null
      this._lastCloseCode.control = event && Number.isFinite(event.code) ? event.code : null
      this._stopHeartbeat()
      const wasConnected = this._channelState.control === CHANNEL_STATES.CONNECTED
      this._channelState.control = CHANNEL_STATES.OFFLINE
      if (event && event.reason) this._error.control = event.reason
      this._emitStatus()
      if (wasConnected) this._emit('disconnected', { channel: 'control', status: this.getStatus() })
      if (this._isPolicyClose(event)) {
        this._emit('authorizationRequired', {
          channel: 'control',
          code: event.code,
          reason: this._error.control || 'policy_rejected',
          status: this.getStatus(),
        })
        return
      }
      this._scheduleReconnect('control')
    }
  }

  _ensureAudioConnection() {
    if (this._manualStop || !this._device || this._audioConsumers.size === 0) return
    if (this._audioWs && (
      this._channelState.audio === CHANNEL_STATES.CONNECTING
      || this._channelState.audio === CHANNEL_STATES.RECONNECTING
      || this._channelState.audio === CHANNEL_STATES.CONNECTED
    )) return

    const reconnecting = this._reconnectAttempt.audio > 0
    this._channelState.audio = reconnecting
      ? CHANNEL_STATES.RECONNECTING
      : CHANNEL_STATES.CONNECTING
    this._emitStatus()

    const generation = ++this._audioGeneration
    let socket
    try {
      socket = this._createWebSocket(buildRock5aWsUrl(this._device, 'audio'))
    } catch (error) {
      this._error.audio = normaliseError(error, 'audio_socket_create_failed')
      this._channelState.audio = CHANNEL_STATES.OFFLINE
      this._emit('error', { channel: 'audio', error: this._error.audio })
      this._scheduleReconnect('audio')
      return
    }
    this._audioWs = socket
    socket.binaryType = 'arraybuffer'

    socket.onopen = () => {
      if (!this._isCurrentSocket('audio', socket, generation)) return
      this._channelState.audio = CHANNEL_STATES.CONNECTED
      this._error.audio = null
      this._audioOpenedAt = this._now()
      this._emitStatus()
      this._emit('audioConnected', this.getStatus())
    }

    socket.onmessage = (event) => {
      if (!this._isCurrentSocket('audio', socket, generation)) return
      const data = event.data
      if (typeof data === 'string') {
        this._handleAudioMessage(data)
        return
      }
      if (!(data instanceof ArrayBuffer) && !ArrayBuffer.isView(data)) return
      const chunk = data instanceof ArrayBuffer
        ? data
        : data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength)
      this._lastFrameAt.audio = this._now()
      this._reconnectAttempt.audio = 0
      this._emit('audioChunk', chunk)
      this._emitFreshnessTransition()
    }

    socket.onerror = (event) => {
      if (!this._isCurrentSocket('audio', socket, generation)) return
      this._error.audio = normaliseError(event, 'audio_websocket_error')
      this._emit('error', { channel: 'audio', error: this._error.audio, event })
      this._emitStatus()
    }

    socket.onclose = (event) => {
      if (!this._isCurrentSocket('audio', socket, generation)) return
      this._audioWs = null
      this._lastCloseCode.audio = event && Number.isFinite(event.code) ? event.code : null
      const wasConnected = this._channelState.audio === CHANNEL_STATES.CONNECTED
      this._channelState.audio = this._audioConsumers.size > 0
        ? CHANNEL_STATES.OFFLINE
        : CHANNEL_STATES.IDLE
      if (event && event.reason) this._error.audio = event.reason
      this._emitStatus()
      if (wasConnected) this._emit('disconnected', { channel: 'audio', status: this.getStatus() })
      if (this._isPolicyClose(event)) {
        this._emit('authorizationRequired', {
          channel: 'audio',
          code: event.code,
          reason: this._error.audio || 'policy_rejected',
          status: this.getStatus(),
        })
        return
      }
      this._scheduleReconnect('audio')
    }
  }

  _handleControlMessage(raw) {
    const timestamp = this._now()
    this._lastFrameAt.control = timestamp
    this._reconnectAttempt.control = 0
    const message = parseRock5aJsonMessage(raw)
    if (!message) {
      this._emit('message', raw)
      this._emitFreshnessTransition()
      return
    }

    if (Number.isFinite(message.seq)) this._lastSequence = message.seq

    if (isRock5aPongMessage(message)) this._emit('pong', message)

    if (message.type === 'connected') {
      this._serverInfo = {
        server: message.server || 'rock5a',
        protocolVersion: String(message.protocol_version || message.version || '1.0'),
        deviceId: message.device_id || this._device?.device_id || this._device?.id || null,
        model: message.model || null,
        capabilities: clonePayload(message.capabilities) || {},
        connectedAt: timestamp,
      }
      this._emit('welcome', clonePayload(this._serverInfo))
      this._emitStatus()
    } else if (message.type === 'data' && message.topic === 'device/rock5a/gesture') {
      this._lastFrameAt.gesture = timestamp
      this._emit('gesture', message.payload)
    } else if (message.type === 'data' && message.topic === 'device/rock5a/status') {
      this._lastFrameAt.terminalStatus = timestamp
      this._terminalStatus = clonePayload(message.payload) || {}
      this._emit('terminalStatus', clonePayload(this._terminalStatus))
      this._emitStatus()
    } else if (message.type === 'data') {
      this._emit('data', {
        topic: message.topic,
        payload: message.payload,
        message,
      })
    } else if (this._isLegacyGestureMessage(message)) {
      const payload = this._legacyPayload(message)
      this._lastFrameAt.gesture = timestamp
      this._emit('gesture', payload)
    } else if (this._isLegacyStatusMessage(message)) {
      const payload = this._legacyPayload(message)
      this._lastFrameAt.terminalStatus = timestamp
      this._terminalStatus = clonePayload(payload) || {}
      this._emit('terminalStatus', clonePayload(this._terminalStatus))
      this._emitStatus()
    }

    this._emit('message', message)
    this._emitFreshnessTransition()
  }

  _handleAudioMessage(raw) {
    const message = parseRock5aJsonMessage(raw)
    if (!message) {
      this._emit('audioMessage', raw)
      return
    }
    this._reconnectAttempt.audio = 0
    if (message.type === 'audio_connected' || message.type === 'connected') {
      this._audioServerInfo = {
        server: message.server || 'rock5a-audio',
        protocolVersion: String(message.protocol_version || message.version || '1.0'),
        deviceId: message.device_id
          || this._serverInfo?.deviceId
          || this._device?.device_id
          || this._device?.id
          || null,
        sampleRate: message.sample_rate || null,
        channels: message.channels || null,
        frameSamples: message.frame_samples || null,
        connectedAt: this._now(),
      }
      this._emit('audioWelcome', clonePayload(this._audioServerInfo))
    } else if (message.type === 'tts_ack') {
      this._emit('ttsAck', message)
    }
    this._emit('audioMessage', message)
    this._emitStatus()
  }

  _isLegacyGestureMessage(message) {
    return message.type === 'gesture'
      || message.type === 'gesture_data'
      || typeof message.gesture === 'string'
      || Boolean(message.right || message.left)
      || (message.topic === 'device/rock5a/gesture' && message.payload)
  }

  _isLegacyStatusMessage(message) {
    return message.type === 'status'
      || message.type === 'terminal_status'
      || message.type === 'device_status'
      || (message.topic === 'device/rock5a/status' && message.payload)
  }

  _legacyPayload(message) {
    if (message.payload && typeof message.payload === 'object') return message.payload
    if (message.data && typeof message.data === 'object') return message.data
    if (message.status && typeof message.status === 'object') return message.status
    const payload = { ...message }
    delete payload.type
    delete payload.topic
    delete payload.seq
    delete payload.timestamp
    delete payload.device_id
    return payload
  }

  _isCurrentSocket(channel, socket, generation) {
    if (channel === 'control') {
      return this._controlWs === socket && this._controlGeneration === generation
    }
    return this._audioWs === socket && this._audioGeneration === generation
  }

  _isPolicyClose(event) {
    return Boolean(event && event.code === 1008)
  }

  _closeSocket(channel, nextState) {
    const property = channel === 'control' ? '_controlWs' : '_audioWs'
    const socket = this[property]
    if (channel === 'control') this._controlGeneration += 1
    else this._audioGeneration += 1
    this[property] = null
    this._channelState[channel] = nextState
    if (!socket) return
    socket.onopen = null
    socket.onmessage = null
    socket.onerror = null
    socket.onclose = null
    try {
      socket.close()
    } catch (_) {
      // Ignore close failures during teardown.
    }
  }

  _startHeartbeat() {
    this._stopHeartbeat()
    this._heartbeatTimer = this._setInterval(() => {
      if (this._channelState.control !== CHANNEL_STATES.CONNECTED) return
      const reference = this._lastFrameAt.control || this._controlOpenedAt
      if (reference && this._now() - reference > this.options.controlFreshMs) {
        this._error.control = 'control_heartbeat_timeout'
        const socket = this._controlWs
        if (socket) {
          try { socket.close() } catch (_) { /* onclose performs reconnect */ }
        }
        return
      }
      this.sendControl(createRock5aPingMessage(this._now()))
    }, this.options.heartbeatIntervalMs)
  }

  _stopHeartbeat() {
    if (!this._heartbeatTimer) return
    this._clearInterval(this._heartbeatTimer)
    this._heartbeatTimer = null
  }

  _startFreshnessMonitor() {
    if (this._freshnessTimer) return
    this._freshnessSignature = this._getFreshnessSignature()
    this._freshnessTimer = this._setInterval(() => {
      if (
        this._channelState.audio === CHANNEL_STATES.CONNECTED
        && this._audioConsumers.size > 0
      ) {
        const reference = this._lastFrameAt.audio || this._audioOpenedAt
        if (reference && this._now() - reference > this.options.audioStaleReconnectMs) {
          this._error.audio = 'audio_frame_timeout'
          const socket = this._audioWs
          if (socket) {
            try { socket.close() } catch (_) { /* onclose performs reconnect */ }
          }
        }
      }
      this._emitFreshnessTransition()
    }, this.options.freshnessCheckIntervalMs)
  }

  _stopFreshnessMonitor() {
    if (!this._freshnessTimer) return
    this._clearInterval(this._freshnessTimer)
    this._freshnessTimer = null
  }

  _getFreshnessSignature() {
    const now = this._now()
    return [
      ageOf(this._lastFrameAt.control, now) <= this.options.controlFreshMs,
      ageOf(this._lastFrameAt.gesture, now) <= this.options.controlFreshMs,
      ageOf(this._lastFrameAt.audio, now) <= this.options.audioFreshMs,
      ageOf(this._lastFrameAt.terminalStatus, now) <= this.options.terminalStatusFreshMs,
    ].join('|')
  }

  _emitFreshnessTransition() {
    const signature = this._getFreshnessSignature()
    if (signature !== this._freshnessSignature) this._emitStatus()
  }

  _clearReconnectTimer(channel) {
    const timer = this._reconnectTimer[channel]
    if (!timer) return
    this._clearTimeout(timer)
    this._reconnectTimer[channel] = null
  }

  _scheduleReconnect(channel) {
    if (this._manualStop || !this._device) return
    if (channel === 'control' && this._consumers.size === 0) return
    if (channel === 'audio' && this._audioConsumers.size === 0) return
    if (this._reconnectTimer[channel]) return

    const attempts = this._reconnectAttempt[channel]
    if (attempts >= this.options.reconnectMaxAttempts) {
      this._error[channel] = `${channel}_reconnect_give_up`
      this._channelState[channel] = CHANNEL_STATES.OFFLINE
      this._emit('reconnectGiveUp', { channel, attempts })
      this._emitStatus()
      return
    }

    const delayMs = Math.min(
      this.options.reconnectBaseDelayMs * (2 ** attempts),
      this.options.reconnectMaxDelayMs
    )
    this._reconnectAttempt[channel] = attempts + 1
    this._channelState[channel] = CHANNEL_STATES.RECONNECTING
    this._emit('reconnectScheduled', {
      channel,
      attempt: this._reconnectAttempt[channel],
      delayMs,
    })
    this._emitStatus()

    this._reconnectTimer[channel] = this._setTimeout(async () => {
      this._reconnectTimer[channel] = null
      if (this._manualStop || !this._device) return
      if (channel === 'control') {
        await this._maybeRediscover()
        this._ensureControlConnection()
      } else {
        this._ensureAudioConnection()
      }
    }, delayMs)
  }

  async _maybeRediscover() {
    if (!this._rediscoverHook || this._rediscoveryPromise) return this._rediscoveryPromise
    if (this._reconnectAttempt.control < this.options.rediscoverAfterAttempts) return null

    this._rediscoveryPromise = Promise.resolve()
      .then(() => this._rediscoverHook(this._device ? { ...this._device } : null))
      .then((result) => {
        const next = this._selectRediscoveredDevice(result)
        if (!next) return null
        const merged = normaliseDevice({ ...this._device, ...next })
        const changed = endpointKey(merged) !== endpointKey(this._device)
        if (changed) {
          const controlAttempt = this._reconnectAttempt.control
          this._switchDevice(merged)
          this._reconnectAttempt.control = controlAttempt
          if (this._audioConsumers.size > 0) this._ensureAudioConnection()
        } else this._device = merged
        this._emit('rediscovered', { device: { ...merged }, changed })
        this._emitStatus()
        return merged
      })
      .catch((error) => {
        this._emit('rediscoveryError', normaliseError(error, 'rediscovery_failed'))
        return null
      })
      .finally(() => {
        this._rediscoveryPromise = null
      })
    return this._rediscoveryPromise
  }

  _selectRediscoveredDevice(result) {
    if (!result) return null
    if (result.ip || result.host) return result
    const devices = Array.isArray(result) ? result : result.devices
    if (!Array.isArray(devices) || devices.length === 0) return null
    const current = this._device || {}
    return devices.find((device) => (
      (current.id && device.id === current.id)
      || (current.host && device.host === current.host)
      || (current.name && device.name === current.name)
    )) || devices[0]
  }
}

let sharedSession = null

export function getRock5aDeviceSession(options) {
  if (!sharedSession) sharedSession = new Rock5aDeviceSession(options)
  return sharedSession
}

export function resetRock5aDeviceSessionForTests() {
  if (sharedSession) sharedSession.disconnectAll()
  sharedSession = null
}

export { CHANNEL_STATES as ROCK5A_CHANNEL_STATES }

export default getRock5aDeviceSession
